import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hero Slider — auto-scrolling image-only slider with pause-on-hover, swipe on mobile,
 * dots + arrow controls. No text overlays on images (image-only per home-page rule).
 */
export default function HeroSlider() {
  const { t } = useI18n();
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const touchStart = useRef(null);

  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then((r) => setSlides(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (paused || slides.length < 2) return;
    timer.current = setTimeout(() => setIndex((i) => (i + 1) % slides.length), 4200);
    return () => clearTimeout(timer.current);
  }, [index, paused, slides.length]);

  if (!slides.length) return null;

  const go = (n) => setIndex(((n % slides.length) + slides.length) % slides.length);
  const active = slides[index];

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
    touchStart.current = null;
  };

  return (
    <section className="relative bg-white py-16 md:py-24 border-t border-slate-100">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 md:mb-12">
          <div>
            <div className="label-eyebrow mb-3">{t.slider.title}</div>
            <h2 className="font-display font-medium text-slate-900 text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl">
              {t.slider.sub}
            </h2>
          </div>
        </div>

        <div
          className="relative rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 shadow-lg group"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="hero-slider"
        >
          <div className="relative aspect-[16/10] md:aspect-[16/8]">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
                className="absolute inset-0"
              >
                <Link
                  to={`/collections/${active.collection_slug}`}
                  aria-label={active.title}
                  className="block w-full h-full"
                  data-testid="slider-image-link"
                >
                  <img
                    src={active.image}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <button
            aria-label="Previous"
            onClick={() => go(index - 1)}
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 grid place-items-center rounded-full bg-white/85 hover:bg-white text-slate-900 shadow-md transition-colors backdrop-blur"
            data-testid="slider-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            aria-label="Next"
            onClick={() => go(index + 1)}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 grid place-items-center rounded-full bg-white/85 hover:bg-white text-slate-900 shadow-md transition-colors backdrop-blur"
            data-testid="slider-next"
          >
            <ChevronRight size={18} />
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 md:bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === index ? "bg-white w-6" : "bg-white/60 w-1.5 hover:bg-white/85"}`}
                data-testid={`slider-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
